import type { Ref } from 'vue';
import type { FieldOverrideOptions } from '#layers/block-engine';
import type { ResolvedMonospaceCollection, ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import { computed } from 'vue';
import { getCollectionFields } from '#layers/data-model/utils/schema/collection';

/**
 * Computes field overrides to hide back-reference fields when opening a nested drawer.
 *
 * The caller provides the source relation field and the target collection that the drawer
 * will render. For O2M/M2O, the target is `field.oppositeCollection`. For M2M, it's the
 * M2M target collection (not the junction).
 */
export function useDrawerFieldOverrides(
	field: Ref<ResolvedMonospaceSingleRelationField | null>,
	targetCollection: Ref<ResolvedMonospaceCollection | null | undefined>,
) {
	return computed(() => {
		if (!field.value || !targetCollection.value) return {};

		const fields = getCollectionFields(targetCollection.value, { includePrimitiveFields: false });

		return fields.reduce((acc, targetField) => {
			const isDirectBackRef = targetField.oppositeRelationFieldId === field.value!.id;

			// For M2M: the back-reference's junction other-leg matches our junction source-leg
			const isM2MBackRef = field.value!.meta?.oppositeJunctionRelationFieldId != null
				&& targetField.meta?.oppositeJunctionRelationFieldId === field.value!.oppositeRelationFieldId;

			if (isDirectBackRef || isM2MBackRef) {
				acc[targetField.id] = { hidden: true };
			}

			return acc;
		}, {} as Record<string, FieldOverrideOptions>);
	});
}
